import {
  EMPTY_STRING,
  RECENTLY_ADDED_HABITS,
  ROUTINE
} from 'constants/general';
import {
  ActivityType,
  RoutineSuggestionsType
} from 'interfaces/common.interface';
import moment from 'moment';
import emojiRegex from 'emoji-regex';
import { getDurationFormat } from './helpers';
import { t } from 'services/i18n.service';
import { Routine } from 'interfaces';
import { DEFAULT_GOAL_SUGGESTIONS } from 'assets/data';

export const getUniqueGoals = (values: string[]) =>
  values.filter((goal, index, goals) => goals.indexOf(goal) === index);

// Recomputes each routine's `value` flag from the set of selected routine titles, and
// reports whether any flag actually changed. Callers use `hasChanged` to avoid
// dispatching a redundant (new-reference) payload that would retrigger their effect —
// see ChooseRoutines, where an unconditional dispatch caused an infinite render loop.
export const syncRoutinePayloadValues = (
  routinesPayload: Routine[],
  selectedTitles: string[]
): { nextPayload: Routine[]; hasChanged: boolean } => {
  const nextPayload = routinesPayload.map((routine) => ({
    ...routine,
    value: selectedTitles.includes(routine.title)
  }));
  const hasChanged = nextPayload.some(
    (routine, index) => routine.value !== routinesPayload[index]?.value
  );
  return { nextPayload, hasChanged };
};

export const formatRoutineTimeSuggestions = (duration: number) =>
  moment()
    .startOf('date')
    .add(duration, 'seconds')
    .format(getDurationFormat(duration));

export const formatOnboardingPackTitle = (
  duration: number,
  pack_name: string
) =>
  `${moment().startOf('day').add(duration, 'seconds').format('H[h]:mm[m]')} ${pack_name}`;

export const getRoutinesDuration = (suggestions: RoutineSuggestionsType) => {
  return Object.values(suggestions)
    .flat()
    .reduce((total, activity) => {
      return (total += parseInt(activity.duration_seconds?.toString()));
    }, 0);
};

export const unifyRoutineActivities = (suggestions: RoutineSuggestionsType) => {
  return Object.entries(suggestions).reduce(
    (routineActivities: ActivityType[], [, activities]) =>
      routineActivities.concat(activities),
    []
  );
};

export const classifyRoutineSuggestions = (
  suggestions: RoutineSuggestionsType
) => {
  const morning_suggestions: RoutineSuggestionsType = {};
  const evening_suggestions: RoutineSuggestionsType = {};

  for (const [goal, activities] of Object.entries(suggestions)) {
    for (const activity of activities) {
      const duration_seconds = parseInt(
        activity.duration_seconds?.toString() || '0'
      );
      const updatedActivity = { ...activity, duration_seconds };

      const target =
        activity.activity_type === ROUTINE.MORNING
          ? morning_suggestions
          : evening_suggestions;

      if (!target[goal]) {
        target[goal] = [];
      }

      target[goal].push(updatedActivity);
    }
  }

  return { morning_suggestions, evening_suggestions };
};

export const mergeRefinedRoutineSuggestions = (
  existingRoutineSuggestions: RoutineSuggestionsType,
  refinedSuggestions: RoutineSuggestionsType,
  routine: string
): RoutineSuggestionsType => {
  // The refine-with-AI endpoint doesn't always return `activity_type`.
  // We already know which routine the refinements are for, so fill it in when missing.
  // Otherwise, `classifyRoutineSuggestions` will treat missing `activity_type` as Evening.
  const refinedWithRoutine: RoutineSuggestionsType = Object.fromEntries(
    Object.entries(refinedSuggestions).map(([goal, activities]) => [
      goal,
      (activities ?? []).map((activity) => ({
        ...activity,
        activity_type: activity.activity_type ?? routine
      }))
    ])
  );

  const {
    morning_suggestions: currentMorning,
    evening_suggestions: currentEvening
  } = classifyRoutineSuggestions(existingRoutineSuggestions);
  const {
    morning_suggestions: refinedMorning,
    evening_suggestions: refinedEvening
  } = classifyRoutineSuggestions(refinedWithRoutine);

  const merged: RoutineSuggestionsType = {};
  const goals = new Set<string>([
    ...Object.keys(currentMorning),
    ...Object.keys(currentEvening),
    ...Object.keys(refinedMorning),
    ...Object.keys(refinedEvening)
  ]);

  for (const goal of goals) {
    if (routine === ROUTINE.MORNING) {
      const morningPart = refinedMorning[goal] ?? currentMorning[goal] ?? [];
      const eveningPart = currentEvening[goal] ?? [];
      merged[goal] = morningPart.concat(eveningPart);
    } else {
      const morningPart = currentMorning[goal] ?? [];
      const eveningPart = refinedEvening[goal] ?? currentEvening[goal] ?? [];
      merged[goal] = morningPart.concat(eveningPart);
    }
  }

  return merged;
};

export const getRoutineValue = (
  routines: { title: string; value: boolean }[]
) =>
  routines
    .find((routine) => routine.value)
    ?.title?.toLowerCase()
    ?.includes(ROUTINE.MORNING)
    ? ROUTINE.MORNING
    : ROUTINE.EVENING;

export const updateSuggestionsWithActivities = (
  originalSuggestions: RoutineSuggestionsType,
  updatedActivities: ActivityType[]
): RoutineSuggestionsType => {
  const updatedSuggestions = { ...originalSuggestions };

  if (!updatedSuggestions[RECENTLY_ADDED_HABITS]) {
    updatedSuggestions[RECENTLY_ADDED_HABITS] = [];
  }

  for (const goal of Object.keys(updatedSuggestions)) {
    if (goal === RECENTLY_ADDED_HABITS) {
      continue;
    }

    const currentActivities = updatedSuggestions[goal];
    const updatedGoalActivities: ActivityType[] = [];
    let hasMatch = false;

    for (const activity of currentActivities) {
      const updated = updatedActivities.find((a) => a.id === activity.id);
      if (updated) {
        updatedGoalActivities.push(updated);
        hasMatch = true;
      }
    }

    updatedSuggestions[goal] = hasMatch ? updatedGoalActivities : [];
  }

  const habitsIdsWithOutRecent = Object.entries(updatedSuggestions)
    .filter(([goal]) => goal !== RECENTLY_ADDED_HABITS)
    .flatMap(([, activities]) => activities.map((activity) => activity.id));

  const restOfUpdateActivities = updatedActivities.filter(
    (updatedActivity) => !habitsIdsWithOutRecent.includes(updatedActivity.id)
  );

  updatedSuggestions[RECENTLY_ADDED_HABITS] = restOfUpdateActivities;
  return updatedSuggestions;
};

export function splitSuggestionsByActivitiesPresence(
  suggestions: RoutineSuggestionsType
): {
  suggestionsWithActivities: RoutineSuggestionsType;
  suggestionsWithoutActivities: RoutineSuggestionsType;
} {
  const suggestionsWithActivities: RoutineSuggestionsType = {};
  const suggestionsWithoutActivities: RoutineSuggestionsType = {};

  for (const [goal, activities] of Object.entries(suggestions)) {
    if (activities?.length) {
      suggestionsWithActivities[goal] = activities;
    } else {
      suggestionsWithoutActivities[goal] = activities;
    }
  }

  return { suggestionsWithActivities, suggestionsWithoutActivities };
}

export const hasRoutineSuggestionsWithActivities = (
  suggestions: RoutineSuggestionsType
) => Boolean(Object.values(suggestions).flat().length);

export const removeEmojis = (input: string) => {
  const regex = emojiRegex();
  return input.replace(regex, EMPTY_STRING).trim();
};

export const filterOutCustomTiming = (routines: Routine[]) =>
  routines.filter(
    (routine) => routine.title !== t('routine_suggestion.custom_timing')
  );

export const resolveGoalEmoji = (goal: string) =>
  DEFAULT_GOAL_SUGGESTIONS.find((defaultGoal) =>
    defaultGoal.toLowerCase().includes(goal.toLowerCase())
  ) ?? goal;

export const hasNonEmptyArrayData = (
  data?: Record<string, unknown> | null
): boolean => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  const values = Object.values(data);
  if (values.length === 0) {
    return false;
  }

  for (const value of values) {
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }
  }

  return false;
};

export const filterDuplicateActivities = (
  morning_activities?: ActivityType[],
  evening_activities?: ActivityType[]
) => {
  const seenIds = new Set<string>();
  const uniqueMorningActivities: ActivityType[] = [];
  const uniqueEveningActivities: ActivityType[] = [];

  (morning_activities ?? []).forEach((activity) => {
    if (!seenIds.has(activity.id)) {
      seenIds.add(activity.id);
      uniqueMorningActivities.push(activity);
    }
  });

  (evening_activities ?? []).forEach((activity) => {
    if (!seenIds.has(activity.id)) {
      seenIds.add(activity.id);
      uniqueEveningActivities.push(activity);
    }
  });

  return {
    uniqueMorningActivities,
    uniqueEveningActivities
  };
};

export const getDuplicateEmojis = (
  suggestions: RoutineSuggestionsType,
  templateId: string
) => {
  const duplicateEmojis: { goal: string; emoji: string }[] = [];
  const foundInGoals: string[] = [];
  const goalEmojiMapper: Record<string, string> = {
    'Build healthy habits': '🌱',
    'Stay focused at work': '🧠',
    'Meditate consistently': '🧘‍♂️',
    'Sleep better': '😴',
    'Exercise more': '🏃‍♀️',
    'Improve work life balance': '⚖️',
    'Improve eye health': '👀',
    'Get organised': '✅',
    'Know thyself': '🪞',
    'Chill out': '🌴',
    'Be more creative': '🎨',
    'Learn new things': '🧩',
    'Track quantified self metrics': '📈',
    'Take medicine': '💊',
    'Self care': '🧖‍♀️',
    'Boost productivity (work, study, tasks)': '🚀',
    'Improve mental well-being (stress, sleep)': '🌿',
    'Focus on health & fitness (exercise, nutrition)': '💪',
    'Manage home tasks (cleaning, chores)': '🧹',
    'Strengthen relationships (family, friend)': '❤️'
  };

  Object.entries(suggestions).forEach(([goal, templates]) => {
    if (templates.some((t) => t.id === templateId)) {
      foundInGoals.push(goal);
    }
  });

  if (foundInGoals.length > 1) {
    foundInGoals.forEach((goal) => {
      const emoji = goalEmojiMapper[goal] || '🎯';
      duplicateEmojis.push({ goal, emoji });
    });
  }

  return duplicateEmojis;
};
